// CompanyGroupList - MODULE
// ***********************************************************************************************************************
import { Box, Grid } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { IListFilterItem } from "../../../models/common/listFilterItem";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import * as companyGroupService from "../../../models/services/companyGroupService";
import { ISessionState } from "../../../models/session/sessionState";
import { ActionContainer } from "../../framework/ActionContainer";
import { AddButton } from "../../framework/Button/AddButton";
import { SettingsButton } from "../../framework/Button/SettingsButton";
import { GridRow } from "../../framework/Grid/GridRow";
import { IListUsingComponentState, List } from "../../framework/List/List";
import { ListCardField } from "../../framework/List/ListCardField";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListDetailsButton } from "../../framework/List/ListDetailsButton";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { ListSearchContainer } from "../../framework/List/ListSearchContainer";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { ListToggleButtons } from "../../framework/List/ListToggleButtons";
import { PageTitle } from "../../framework/Page/PageTitle";
import { EllipsisTypography } from "../../framework/Typography/EllipsisTypography";
import { IPageNavMenuItemActionsAction } from "source/components/framework/PageNavMenuItem/Actions";
import { ICompanyGroupListItems } from "source/models/companyGroup/companyGroupListItems";
import { ICompanyGroupListItem } from "source/models/companyGroup/companyGroupListItem";
import { CompanyGroupDialogMenuItem, CompanyGroupEditDialog } from "../CompanyGroupEditDialog";

// CompanyGroupList
export interface ICompanyGroupListProp {
    savedFilter: IListFilterItem;
    sessionState: ISessionState;
    disableGlobalLoading?: boolean;
    preloaded?: boolean;
    waitingPreloadedData?: boolean;
    list?: ICompanyGroupListItems;
    dataId?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    reportTabs: React.ReactNode;
    settingsActions: IPageNavMenuItemActionsAction[];
    onRefreshSessionState: () => void;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    //onOpenFiltersDialog: () => void;
    onToggleSettingsDialog: () => void;
}

interface ICompanyGroupListState extends IListUsingComponentState<ICompanyGroupListItem> {
    // List
    intialized: boolean;
    isLoading: boolean;
    waitingPreloadedData: boolean;
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    listModeIsLine: boolean;
    checkedIds: string[];

    // Dialog
    editDialogDefaultMenuItem: CompanyGroupDialogMenuItem;
}

export class CompanyGroupList extends React.Component<ICompanyGroupListProp, ICompanyGroupListState> {
    constructor(props: ICompanyGroupListProp) {
        super(props);
        this.state = {
            intialized: props.list && !!props.preloaded,
            isLoading: false,
            waitingPreloadedData: !Base.isNullOrUndefined(props.waitingPreloadedData) ? props.waitingPreloadedData : (props.list ? false : props.preloaded),
            page: props.list ? props.list.page : 1,
            hasMore: props.list ? props.list.hasMore : true,
            filter: "",
            pageSize: props.list ? props.list.pageSize : infiniteListPageSize,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            listModeIsLine: true,
            items: props.list ? props.list.items : [],
            selectedId: null,
            checkedIds: [],
            detailsItem: null,
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            editDialogDefaultMenuItem: CompanyGroupDialogMenuItem.General
        };
    }

    searchItems = async (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean,
        resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setCompanyGroupDialogData: boolean = true) => {
        const state = this.state;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => companyGroupService.getCompanyGroupListItems(!refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, [], sortColumn, sortOrderIsAsc),
            false, this.props.disableGlobalLoading, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogId: setCompanyGroupDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : this.state.editDialogId,
            editDialogHasPrevious: setCompanyGroupDialogData ? index > 0 : this.state.editDialogHasPrevious,
            editDialogHasNext: setCompanyGroupDialogData ? (index < items.items.length - 1 || result.hasMore) : this.state.editDialogHasNext,
            editDialogDefaultMenuItem: setCompanyGroupDialogData ? CompanyGroupDialogMenuItem.General : this.state.editDialogDefaultMenuItem
        });
        this.props.onSetSortData(sortColumn, sortOrderIsAsc);
    };

    refreshList = async (setCompanyGroupDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, true, false, setCompanyGroupDialogData);
    };

    componentDidMount(): void {
        const state = this.state;
        if (this.props.preloaded) return;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentDidUpdate(prevProps: ICompanyGroupListProp, prevState: ICompanyGroupListState) {
        const props = this.props;
        const state = this.state;
        if (state.waitingPreloadedData) {
            if (!props.list || (props.dataId ?? "") === (prevProps.dataId ?? "")) return;
            this.setState({
                intialized: true,
                waitingPreloadedData: false,
                page: props.list.page,
                hasMore: props.list.hasMore,
                items: props.list.items,
                pageSize: props.list.pageSize,
                sortColumn: props.list.sortColumn,
                sortOrderIsAsc: props.list.sortOrderIsAsc
            });
        } else {
            const prevDataId = prevProps.dataId ? prevProps.dataId : "";
            const dataId = props.dataId ? props.dataId : "";
            const prevSortColumn = prevProps.sortColumn;
            const sortColumn = props.sortColumn;
            const prevSortOrderIsAsc = prevProps.sortOrderIsAsc;
            const sortOrderIsAsc = props.sortOrderIsAsc;
            if (dataId === prevDataId && prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
            this.searchItems(state.pageSize, state.page, state.filter, sortColumn, sortOrderIsAsc, true, false);
        }
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds,
            detailsItem: this.state.items.find(i => i.id === selectedId)
        });
    };
    
    handleListModeChange = (listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    handleAdd = () => {
        this.setState({ ...List.getShowEditDialogStateChange(this.state, Base.emptyGuid), editDialogDefaultMenuItem: CompanyGroupDialogMenuItem.General });
    };

    handleOpenLine = (id: string) => {
        this.setState({ ...List.getShowEditDialogStateChange(this.state, id), editDialogDefaultMenuItem: CompanyGroupDialogMenuItem.General });
    };

    handleCompanyGroupDialogOk = () => {
        this.refreshList(false);
    };

    handleCompanyGroupDialogCancel = (userDataWasModified: boolean) => {
        this.setState({
            showEditDialog: false
        });
        if (!userDataWasModified) return;
        this.refreshList(false);
    };

    handleCompanyGroupDialogNewSaved = (newCompanyGroupId: string) => {
        this.setState({
            showEditDialog: true,
            editDialogId: newCompanyGroupId,
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            editDialogDefaultMenuItem: CompanyGroupDialogMenuItem.Companies
        });
        this.refreshList(false);
    };

    handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems(state.pageSize, state.page + 1, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <PageTitle title={Translations.CompanyGroups + (props.savedFilter ? " - " + props.savedFilter.title : (state.filter ? " - " + Translations.Filtered : ""))}>
                    {props.settingsActions.length > 0 &&
                        <SettingsButton onClick={props.onToggleSettingsDialog} sx={{ marginRight: "1.5rem" }}>
                            {Translations.Settings}
                        </SettingsButton>
                    }
                    <AddButton onClick={this.handleAdd}>
                        {Translations.AddCompanyGroup}
                    </AddButton>
                </PageTitle>
                <MainListContainer>
                    <ActionContainer
                        margin="1rem .5rem 1rem 1rem"
                        leftItems={
                            props.reportTabs
                        }
                        rightItems={
                            <>
                                {/* <ListActionButtons onFilter={props.onOpenFiltersDialog} /> */}
                                <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                            </>
                        }
                    />
                    <ListSearchContainer>
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    </ListSearchContainer>
                    <List
                        intialized={state.intialized}
                        isLoading={state.isLoading}
                        hasMore={state.hasMore}
                        height="calc(100vh - 296px)" //Main List
                        globalLoadingInUse={!props.disableGlobalLoading}
                        header={state.listModeIsLine
                            ? {
                                columns: [
                                    { size: 4, column: "code", content: Translations.Code },
                                    { size: 4, column: "name", content: Translations.Name },
                                    { size: 4, column: "comment", content: Translations.Description }
                                ]
                            }
                            : null}
                        lines={state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    columns: [
                                        { size: 4, column: "code", content: <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue> },
                                        { size: 4, column: "name", content: item.name },
                                        { size: 4, column: "comment", content: item.comment }
                                    ],
                                    id: item.id
                                };
                            })
                            : null
                        }
                        cards={!state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    sm: 4,
                                    rows: [
                                        {
                                            cols: [
                                                { size: 6, title: Translations.Code, value: <Box display="flex" overflow="hidden"><ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue></Box> },
                                                { size: 6, title: Translations.Name, value: item.name }
                                            ]
                                        },
                                        {
                                            cols: [
                                                { title: Translations.Description, value: item.comment },
                                            ]
                                        },
                                        {
                                            cols: [
                                                {
                                                    title: Translations.Companies,
                                                    value:
                                                    <EllipsisTypography title={Base.getStringWithSeparators(item.companies.map(i => i.name), ", ")}>
                                                        {item.companies.map((company, index) =>
                                                            <span key={company.id}>
                                                                {index > 0 &&
                                                                    <span>,&nbsp;</span>
                                                                }
                                                                <span className={company.disabled ? "notInUse" : null}>{company.name}</span>
                                                            </span>
                                                        )}
                                                    </EllipsisTypography>
                                                }
                                            ]
                                        }],
                                    id: item.id
                                };
                            })
                            : null
                        }
                        lineDetails={state.detailsItem
                            ? <Box position="relative">
                                <GridRow>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Code} value={state.detailsItem.code} />
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Name} value={state.detailsItem.name} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Description} value={state.detailsItem.comment} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Companies} value={
                                                    <div className="value canWrap" title={Base.getStringWithSeparators(state.detailsItem.companies.map(i => i.name), ", ")}>
                                                        {state.detailsItem.companies.map((company, index) =>
                                                            (index < 10
                                                                ? <span key={company.id}>
                                                                    {index > 0 &&
                                                                <span>,&nbsp;</span>
                                                                    }
                                                                    <span className={company.disabled ? "notInUse" : null}>{company.name}</span>
                                                                </span>
                                                                : null
                                                            )
                                                        )}
                                                        {state.detailsItem.companies.length > 6 &&
                                                            <span>{" + (" + (state.detailsItem.companies.length - 10).toString(10) + ")"}</span>
                                                        }
                                                    </div>
                                                } />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                </GridRow>
                                <ListDetailsButton
                                    onClick={() => this.handleOpenLine(state.selectedId)}
                                />
                            </Box>
                            : null}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={this.handleSetSelectedIds}
                        onSearchLines={this.handleSearchLines}
                        onOpenLine={this.handleOpenLine}
                    />
                </MainListContainer>
                {(state.showEditDialog && !!state.editDialogId) &&
                    <CompanyGroupEditDialog
                        sessionState={props.sessionState}
                        defaultMenuItem={state.editDialogDefaultMenuItem}
                        companyGroupId={state.editDialogId}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onSaved={this.handleCompanyGroupDialogOk}
                        onClose={this.handleCompanyGroupDialogCancel}
                        onRefreshList={() => this.refreshList(false)}
                        onNewSaved={this.handleCompanyGroupDialogNewSaved}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                    />
                }
            </>
        );
    }
}